/**
 * RenderRoot — composites the two layers into one view and owns the camera.
 *
 *   [ Three.js VFX canvas ]   <- transparent, on top (additive glow)
 *   [ Pixi 2D world canvas ]  <- opaque background world
 *
 * Both canvases are absolutely positioned and share the mount element's size.
 * A ResizeObserver keeps them in sync with the (rotating, resizing) mobile
 * viewport, and devicePixelRatio is honoured but capped in each layer.
 */
import { PixiLayer } from "./PixiLayer";
import { ThreeLayer, type DroneBeam } from "./ThreeLayer";
import type { GameState } from "@/engine/GameState";

export interface Camera {
  x: number; // world-space target
  y: number;
  zoom: number; // screen pixels per world unit
}

export class RenderRoot {
  readonly pixi = new PixiLayer();
  readonly three = new ThreeLayer();
  camera: Camera = { x: 0, y: 0, zoom: 0.55 };

  private mountEl!: HTMLElement;
  private ro?: ResizeObserver;
  private destroyed = false; // guard so destroy() is idempotent (async unmount can call it twice)
  private viewW = 1;
  private viewH = 1;
  private lastT = performance.now();
  /** Player zoom-IN multiplier on top of the auto fit-zoom (1 = fully zoomed out). */
  private userZoomMul = 1;

  /** Pinch / wheel zoom. Clamped so the auto fit-zoom is the max zoom-OUT. */
  zoomBy(factor: number): void {
    this.userZoomMul = Math.max(1, Math.min(4, this.userZoomMul * factor));
  }

  async mount(el: HTMLElement): Promise<void> {
    this.mountEl = el;
    // Only add positioning if the container is unpositioned. Forcing "relative"
    // here would override a container whose CSS uses `position:absolute; inset:0`
    // and collapse its height to content (0) — which blanks the whole view.
    if (getComputedStyle(el).position === "static") {
      el.style.position = "relative";
    }
    el.style.touchAction = "none"; // we handle gestures ourselves

    this.measure();

    await this.pixi.init(this.viewW, this.viewH, window.devicePixelRatio);
    this.three.resize(this.viewW, this.viewH);

    for (const c of [this.pixi.canvas, this.three.canvas]) {
      c.style.position = "absolute";
      c.style.inset = "0";
      c.style.width = "100%";
      c.style.height = "100%";
      c.style.display = "block";
    }
    el.appendChild(this.pixi.canvas);
    el.appendChild(this.three.canvas);

    this.ro = new ResizeObserver(() => this.handleResize());
    this.ro.observe(el);
  }

  private handleResize(): void {
    this.measure();
    this.pixi.resize(this.viewW, this.viewH);
    this.three.resize(this.viewW, this.viewH);
  }

  /** Measure the mount, falling back to the viewport if it reports a degenerate size. */
  private measure(): void {
    const rect = this.mountEl.getBoundingClientRect();
    this.viewW = Math.max(1, Math.round(rect.width > 1 ? rect.width : window.innerWidth));
    this.viewH = Math.max(1, Math.round(rect.height > 1 ? rect.height : window.innerHeight));
  }

  /** Called each animation frame by the loop's render callback. */
  render(state: GameState, _alpha: number): void {
    const now = performance.now();
    const dt = Math.min((now - this.lastT) / 1000, 0.1);
    this.lastT = now;

    // Fit-zoom: frame the player's biggest active area ×1.3 — this is the max
    // zoom-OUT. The player may pinch/wheel to zoom IN on top of it (userZoomMul).
    const R = Math.max(60, state.viewRadius);
    const fit = Math.min(this.viewW, this.viewH) / (2 * R * 1.3);
    const fitZoom = Math.max(0.05, Math.min(1.1, fit));
    let targetZoom = Math.min(4, fitZoom * this.userZoomMul);

    // Camera smoothly follows the mothership (Explore feel; no snapping on warp).
    let camTX = state.mothership.pos.x;
    let camTY = state.mothership.pos.y;

    // A scripted cutscene (the Eater's awakening) can seize the camera and pan it to a
    // fixed world point; it overrides the normal follow + capital-ship framing.
    const override = state.cameraOverride;
    // Capital-ship combat framing: when locked on / fighting a cruiser, centre on
    // the midpoint of us and it, and zoom OUT if needed so both stay on screen.
    const focus = override ? null : state.combatFocus;
    if (override) {
      camTX = override.x;
      camTY = override.y;
    } else if (focus) {
      const px = state.mothership.pos.x;
      const py = state.mothership.pos.y;
      camTX = (px + focus.x) / 2;
      camTY = (py + focus.y) / 2;
      const half = Math.hypot(focus.x - px, focus.y - py) / 2 + 140; // ship-size margin
      const fitBoth = Math.min(this.viewW, this.viewH) / (2 * half * 1.15);
      targetZoom = Math.max(0.05, Math.min(targetZoom, fitBoth));
    }

    this.camera.x += (camTX - this.camera.x) * Math.min(1, dt * 4);
    this.camera.y += (camTY - this.camera.y) * Math.min(1, dt * 4);
    this.camera.zoom += (targetZoom - this.camera.zoom) * Math.min(1, dt * 6);

    // Feed EVERY hull-beam impact spot to the 3D VFX layer so each beam sparks.
    const impacts: { x: number; y: number }[] = [];
    for (const tgt of state.miningTargets) {
      for (const s of tgt.impacts) impacts.push({ x: s.x, y: s.y });
    }
    this.three.setImpacts(impacts);

    // Feed the orbiting drones' thin 3D beams to the VFX layer.
    const beams: DroneBeam[] = [];
    for (const u of state.droneBay.units) {
      if (u.beamTarget) {
        beams.push({ ax: u.pos.x, ay: u.pos.y, bx: u.beamTarget.x, by: u.beamTarget.y });
      }
    }
    this.three.setDroneBeams(beams);
    this.three.setStarGlow(state.layer === "system"); // star only in the outer map

    // Combat VFX: enemy deaths (fireball + shake) and bolt-strike sparks.
    for (const e of state.drainCombatFx()) {
      if (e.kind === "kill") this.three.spawnExplosion(e.x, e.y, 1.2);
      else this.three.spawnHit(e.x, e.y);
    }

    // Hull fires: any ship under 50% HP burns — emitted sparsely so the ship
    // stays visible through the flames.
    const ms = state.mothership;
    if (ms.hp > 0 && ms.hp < ms.maxHp * 0.5 && Math.random() < 0.45) {
      this.three.spawnFire(ms.pos.x, ms.pos.y);
    }
    for (const en of state.enemyShips) {
      if (en.hp > 0 && en.hp < en.maxHp * 0.5 && Math.random() < 0.4) {
        this.three.spawnFire(en.pos.x, en.pos.y);
      }
    }

    this.pixi.render(state, this.camera, this.viewW, this.viewH);
    this.three.render(this.camera, this.viewW, this.viewH, dt);
  }

  /** Diagnostics for the on-screen debug readout. */
  get view(): { w: number; h: number } {
    return { w: this.viewW, h: this.viewH };
  }
  get pixiReady(): boolean {
    return this.pixi.ready;
  }

  /** Convert a screen point (e.g. touch) into world coordinates. */
  screenToWorld(sx: number, sy: number): { x: number; y: number } {
    return {
      x: (sx - this.viewW / 2) / this.camera.zoom + this.camera.x,
      y: (sy - this.viewH / 2) / this.camera.zoom + this.camera.y,
    };
  }

  destroy(): void {
    if (this.destroyed) return; // idempotent — a stale async load can trigger a second destroy
    this.destroyed = true;
    this.ro?.disconnect();
    this.pixi.destroy();
    this.three.destroy();
  }
}
